import LazyVideo from "@/components/LazyVideo";
import React, { useState, useEffect, useRef } from 'react';
import { Seo } from '../components/Seo';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { servicesData } from '../data/content';
import { useContent } from '@/context/ContentContext';
import { ArrowLeft, ArrowUpLeft, Calendar } from 'lucide-react';

type Service = (typeof servicesData)[number];

const ServiceRow: React.FC<{ service: Service; index: number }> = ({ service, index }) => {
  return (
    <motion.li
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="border-b border-[#412018]/15 relative group transition-colors before:content-[''] before:absolute before:inset-y-0 before:-inset-x-6 md:before:inset-x-0 before:bg-transparent group-hover:before:bg-[#412018]/[0.03] before:transition-colors hover:before:bg-[#412018]/[0.03]"
    >
      <Link
        to={`/services/${service.id}`}
        aria-label={service.title}
        className="absolute inset-0 z-0"
      />
      <div className="py-8 md:py-12 px-1 md:px-3 relative pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
        {/* Desktop — compact 2 columns: title+desc | price+actions */}
        <div className="hidden md:grid grid-cols-12 gap-10 items-start">
          {/* Title + description — right in RTL */}
          <div className="col-span-8">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-4">
              {service.subtitle}
            </p>
            <h3 className="font-serif leading-[1] tracking-wide text-[#412018] text-4xl lg:text-[2.8rem] mb-4">
              {service.title}
            </h3>
            <p className="text-[#412018]/70 font-light leading-relaxed text-[15px] line-clamp-2 max-w-2xl">
              {service.description}
            </p>
          </div>

          {/* Price + actions — center */}
          <div className="col-span-4 flex flex-col items-stretch gap-2 mt-[34px]">
            {(() => {
              const displayPrice = service.price || 'מחיר אישי';
              const displayDuration = service.duration || '75 דק׳';
              return (
                <div className="flex flex-col gap-1 mb-1">
                  <span className="font-sans font-light text-[1.4rem] lg:text-[1.55rem] text-[#412018] tabular-nums leading-none whitespace-nowrap text-left">
                    {displayPrice}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#412018]/55 text-left">
                    {displayDuration}
                  </span>
                </div>
              );
            })()}
            <Link
              to="/appointment"
              className="group/cta relative overflow-hidden bg-[#412018] text-[#fbbf25] px-4 py-2.5"
            >
              <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover/cta:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.7,0,0.2,1)]" />
              <span className="relative flex items-center justify-center gap-2 group-hover/cta:text-[#412018] transition-colors duration-500">
                <Calendar size={12} strokeWidth={1.6} />
                <span className="text-[10px] font-bold uppercase tracking-[0.28em]">לקביעת תור</span>
              </span>
            </Link>
            <Link
              to={`/services/${service.id}`}
              className="group/cta inline-flex items-center justify-center gap-2 text-[#412018]/75 hover:text-[#412018] py-1 transition-colors"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.28em]">קרא עוד</span>
              <ArrowUpLeft size={12} strokeWidth={1.6} className="transition-transform duration-300 group-hover/cta:-translate-x-0.5 group-hover/cta:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-3">
            {service.subtitle}
          </p>
          <h3 className="font-serif leading-[1.05] tracking-wide text-[#412018] text-[2.2rem] mb-3">
            {service.title}
          </h3>
          <p className="text-[#412018]/70 font-light leading-relaxed text-[14px] line-clamp-3 mb-6">
            {service.description}
          </p>

          <div className="flex items-end justify-between gap-3">
            {/* Price + duration (duration on top) */}
            {(() => {
              const displayPrice = service.price || 'מחיר אישי';
              const displayDuration = service.duration || '75 דק׳';
              const isNumericPrice = /\d/.test(displayPrice);
              const priceTooLong = displayPrice.length > 8;
              return (
                <div className="flex flex-col items-start gap-1.5 shrink min-w-0 max-w-[40%]">
                  <span className="text-[9px] uppercase tracking-[0.22em] text-[#412018]/55">
                    {displayDuration}
                  </span>
                  <span
                    className={`font-sans font-light text-[#412018] tabular-nums [overflow-wrap:anywhere] [word-break:break-word] ${
                      isNumericPrice
                        ? priceTooLong
                          ? 'text-[1.25rem] leading-none'
                          : 'text-[1.6rem] leading-none'
                        : 'text-[1.05rem] leading-[1.15]'
                    }`}
                  >
                    {displayPrice}
                  </span>
                </div>
              );
            })()}


            {/* Buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to={`/services/${service.id}`}
                className="inline-flex items-center justify-center gap-1.5 text-[#412018]/75 px-3 py-2.5 border border-[#412018]/25 shrink-0 whitespace-nowrap"
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.25em]">קרא עוד</span>
              </Link>
              <Link
                to="/appointment"
                className="group/cta relative overflow-hidden bg-[#412018] text-[#fbbf25] px-3.5 py-2.5 shrink-0 whitespace-nowrap"
              >
                <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover/cta:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.7,0,0.2,1)]" />
                <span className="relative flex items-center justify-center gap-1.5 group-hover/cta:text-[#412018] transition-colors duration-500">
                  <Calendar size={12} strokeWidth={1.6} />
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em]">לקביעת תור</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.li>
  );
};

type GroupId = 'hair' | 'face' | 'advanced';

const groups: {
  id: GroupId;
  number: string;
  label: string;
  subtitle: string;
  desc: string;
  categories: string[];
  media: string;
  mediaType: 'video' | 'image';
}[] = [
  {
    id: 'hair',
    number: "01",
    label: "טיפולי שיער וקרקפת",
    subtitle: "Hair & Scalp Therapy",
    desc: "שיקום החיוניות של הכתר — עיסוי תאילנדי עם צמחים ערביים, החלקות אורגניות, טיפול בקשקשים והזנה עמוקה לסיב השערה.",
    categories: ['hair'],
    media: 'https://cdn.shopify.com/videos/c/o/v/0ec560a3c566433ea31b13dd7201f211.webm',
    mediaType: 'video',
  },
  {
    id: 'face',
    number: "02",
    label: "טיפולי פנים",
    subtitle: "Face & Skin Therapy",
    desc: "כל טיפול מתחיל באבחון מקצועי של מצב העור ובהתאמה לצרכים האישיים, במטרה לשפר את בריאות העור, המרקם, הגוון, המוצקות והזוהר הטבעי.",
    categories: ['face', 'holistic'],
    media: 'https://cdn.shopify.com/videos/c/o/v/7c179f614d44422e9bb9ecfdc4ceb037.webm',
    mediaType: 'video',
  },
  {
    id: 'advanced',
    number: "03",
    label: "אסתטיקה מתקדמת ולייזר",
    subtitle: "Advanced Aesthetics & Laser",
    desc: "טיפולים מתקדמים המשלבים אבחון מקצועי, טכנולוגיית לייזר, גלי רדיו, פלאזמה וטיפולים ביולוגיים לחידוש ושיקום העור.",
    categories: ['advanced'],
    media: 'https://cdn.shopify.com/videos/c/o/v/ee9067a524544e8f8374b5dcaefed763.webm',
    mediaType: 'video',
  },
];

export const ServicesPage: React.FC = () => {
  const { services: servicesData } = useContent();
  const [activeGroup, setActiveGroup] = useState<GroupId>('hair');
  const [isSticky, setIsSticky] = useState(false);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    const handleScroll = () => {
      if (sentinelRef.current) {
        const rect = sentinelRef.current.getBoundingClientRect();
        setIsSticky(rect.bottom <= 80);
      }
      const scrollPosition = window.scrollY + window.innerHeight / 3;
      let current: GroupId = 'hair';
      for (const g of groups) {
        const el = sectionRefs.current[g.id];
        if (el && el.offsetTop <= scrollPosition) current = g.id;
      }
      setActiveGroup(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // (removed) auto-scroll into view of the active filter pill —
  // it caused the page to jump back when scrolling between categories.

  const scrollToGroup = (id: GroupId) => {
    const el = sectionRefs.current[id];
    if (el) {
      const offset = 140;
      const top = el.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Seo
        title="טיפולים בקליניקה | RIHAM שירותים אסתטיים וטבעיים"
        description="כל הטיפולים בקליניקה של רהאם מנסור — אסתטיקה, לייזר, פנים, שיער ורפואת צמחים. בחרי את הטיפול המתאים לך."
        path="/services"
      />
    <div className="bg-[#EAE6D7] min-h-[100dvh] pt-32 pb-24 text-[#412018] relative" dir="rtl" style={{ scrollSnapType: 'none' }}>
      {/* Texture */}
      <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

      {/* HERO — full-bleed video, headline + filter links overlaid, fade-out into page bg */}
      <section className="relative w-full min-h-[640px] h-[88vh] max-h-[920px] overflow-hidden -mt-32 mb-12 md:mb-20">
        <LazyVideo
          src={groups[0].media}
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
         aria-hidden="true" />
        {/* Light cream wash so dark text remains legible over the video */}
        <div className="absolute inset-0 bg-[hsl(47_31%_88%/0.72)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(47_31%_88%/0.45)] via-transparent to-[hsl(47_31%_88%/0.55)]" />
        {/* Bottom fade into page bg */}
        <div
          className="absolute -bottom-px left-0 right-0 h-[60%] pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, hsl(47 31% 88%) 0%, hsl(47 31% 88% / 0.98) 18%, hsl(47 31% 88% / 0.85) 38%, hsl(47 31% 88% / 0.5) 62%, hsl(47 31% 88% / 0) 100%)',
          }}
        />
        {/* Paper texture matching the page */}
        <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

        {/* Centered headline */}
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6 pt-28 pb-24 md:pb-32">
          <motion.span
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-6"
          >
            הקליניקה של רהאם מנסור
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[#412018] leading-[0.9] tracking-wide text-[3.2rem] sm:text-7xl md:text-8xl lg:text-[9rem] xl:text-[11rem]"
          >
            השירותים <span className="text-[#67392b] italic">שלנו</span>
          </motion.h1>

          {/* Editorial category links */}
          <motion.nav
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
            ref={filterContainerRef}
            className="mt-10 md:mt-14 w-full max-w-5xl flex flex-col md:flex-row items-stretch md:items-baseline justify-center gap-4 md:gap-10 lg:gap-14"
          >
            {groups.map((g, i) => {
              const isActive = activeGroup === g.id;
              return (
                <React.Fragment key={g.id}>
                  {i > 0 && <span className="hidden md:block w-px h-7 bg-[#412018]/25 self-center" aria-hidden />}
                  <button
                    data-id={g.id}
                    onClick={() => scrollToGroup(g.id)}
                    className="group relative flex-1 md:flex-initial text-center transition-all duration-300 py-2"
                  >
                    <span className={`block font-serif tracking-wide transition-colors text-[2.3rem] sm:text-[2.6rem] md:text-3xl lg:text-4xl leading-[1.05] ${
                      isActive ? 'text-[#67392b] italic' : 'text-[#412018] group-hover:text-[#67392b]'
                    }`}>
                      {g.label}
                    </span>
                    <span className={`mx-auto mt-3 block h-px bg-[#67392b] transition-all duration-500 ${
                      isActive ? 'w-12 opacity-100' : 'w-0 opacity-0 group-hover:w-12 group-hover:opacity-100'
                    }`} />
                  </button>
                </React.Fragment>
              );
            })}
          </motion.nav>
        </div>
      </section>

      <div className="container mx-auto px-6 relative z-10 max-w-[1400px]">
        <div ref={sentinelRef} className="h-1 w-full" aria-hidden />

        {/* Sections */}
        <div className="space-y-32 md:space-y-44">
          {groups.map((group) => {
            const items = servicesData.filter((s) => group.categories.includes(s.category));
            if (!items.length) return null;

            return (
              <section
                key={group.id}
                ref={(el) => { sectionRefs.current[group.id] = el; }}
                className="scroll-mt-44"
              >
                {/* Top meta bar */}
                <div className="flex items-center gap-4 mb-10 md:mb-14">
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-[#412018]/60 font-bold">
                    {group.subtitle}
                  </span>
                  <span className="flex-1 h-px bg-[#412018]/15" />
                  <span className="hidden md:inline text-[10px] uppercase tracking-[0.3em] text-[#412018]/50">
                    {items.length} טיפולים
                  </span>
                </div>

                {/* Two columns: services list LEFT, sticky title+desc+video RIGHT (all move together) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-start">
                  {/* RIGHT (first in RTL DOM): sticky block with title + desc + video */}
                  <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start">
                    <div className="px-0 lg:px-0">
                      {(() => {
                        const [first, ...rest] = group.label.split(' ');
                        return (
                          <h2 className="font-serif leading-[0.95] tracking-wide text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] mb-7">
                            <span className="text-[#412018]">{first}</span>{' '}
                            <span className="text-[#67392b] italic">{rest.join(' ')}</span>
                          </h2>
                        );
                      })()}
                      <div className="w-12 h-px bg-[#67392b] mb-6" />
                      <p className="text-[#412018]/75 text-base md:text-lg font-light leading-relaxed mb-8">
                        {group.desc}
                      </p>
                    </div>

                    {/* Full-bleed video on mobile, contained on desktop */}
                    <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] overflow-hidden bg-[#412018] -mx-6 lg:mx-0">
                      {group.mediaType === 'video' ? (
                        <LazyVideo src={group.media} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover"  aria-hidden="true" />
                      ) : (
                        <img src={group.media} alt={group.label} className="absolute inset-0 w-full h-full object-cover" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#412018]/80 via-[#412018]/15 to-transparent pointer-events-none" />
                      <div className="absolute top-5 right-5 left-5 flex items-start justify-between text-[#EAE6D7]">
                        <span className="text-[10px] uppercase tracking-[0.35em] text-[#fbbf25] font-bold">
                          {group.subtitle}
                        </span>
                      </div>
                    </div>
                    <Link
                      to="/appointment"
                      className="inline-flex items-center gap-3 mt-6 text-[11px] uppercase tracking-[0.3em] text-[#67392b] hover:text-[#412018] font-bold transition-colors"
                    >
                      <span className="w-8 h-px bg-current" />
                      לקביעת תור
                    </Link>
                  </aside>

                  {/* LEFT: services list — pushed down to start at the video line */}
                  <div className="lg:col-span-7">
                    {/* Invisible spacer matching title + desc height so the list starts at the video line */}
                    <div className="hidden lg:block invisible" aria-hidden>
                      {(() => {
                        const [first, ...rest] = group.label.split(' ');
                        return (
                          <h2 className="font-serif leading-[0.95] tracking-wide text-6xl md:text-7xl lg:text-[5.5rem] mb-7">
                            <span>{first}</span> <span className="italic">{rest.join(' ')}</span>
                          </h2>
                        );
                      })()}
                      <div className="w-12 h-px mb-6" />
                      <p className="text-base md:text-lg font-light leading-relaxed mb-8">{group.desc}</p>
                    </div>
                    <ul className="border-t border-[#412018]/15">
                      {items.map((service, idx) => (
                        <ServiceRow key={service.id} service={service} index={idx} />
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* Closing CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-32 text-center max-w-3xl mx-auto border-t border-[#412018]/15 pt-16"
        >
          <h3 className="font-serif text-3xl md:text-5xl text-[#412018] mb-5 leading-tight">
            לא בטוחה איזה טיפול <span className="text-[#67392b] italic">מתאים לך?</span>
          </h3>
          <p className="text-[#412018]/65 font-light mb-8">
            נבנה עבורך תכנית טיפול אישית, בהתאמה מלאה לעור, לשיער ולאורח החיים שלך.
          </p>
          <Link
            to="/appointment"
            className="inline-flex items-center gap-3 bg-[#412018] hover:bg-[#67392b] text-[#fbbf25] px-8 py-4 rounded-full transition-colors duration-300 group"
          >
            <Calendar size={16} />
            <span className="text-xs font-bold tracking-[0.25em] uppercase">קביעת ייעוץ</span>
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>
      </div>
    </div>
    </>
  );
};
